import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

function claimantLine(c, winner) {
  const isWinner =
    winner &&
    c.region_key === winner.region_key &&
    c.target_cod_interno === winner.target_cod_interno;
  return `${esc(c.region_key)} → ${esc(c.target_cod_interno)} ${esc(c.start_cep)}–${esc(c.end_cep)}${
    isWinner ? " (vencedor)" : ""
  }`;
}

export default function CepCoverageLeafletMap({ points = [], onViewCep }) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const onViewRef = useRef(onViewCep);
  onViewRef.current = onViewCep;

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return undefined;
    const map = L.map(hostRef.current, {
      center: [-14.24, -51.93],
      zoom: 4,
      minZoom: 3,
      maxZoom: 12,
      zoomControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    const t = window.setTimeout(() => map.invalidateSize(), 120);
    return () => {
      window.clearTimeout(t);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return undefined;
    layer.clearLayers();
    const bounds = [];
    for (const p of points) {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
      const radius = p.unattended ? 8 : p.overlap ? 11 : p.split || p.unattendedPartial ? 9 : 7;
      const marker = L.circleMarker([p.lat, p.lng], {
        radius,
        color: p.unattended ? "#c62828" : p.overlap ? "#c62828" : p.split || p.unattendedPartial ? "#ef6c00" : p.stroke,
        weight: p.unattended || p.overlap ? 3 : p.split || p.unattendedPartial ? 2 : 1.5,
        fillColor: p.unattended ? "#ffffff" : p.fill,
        fillOpacity: p.unattended ? 0.15 : p.overlap ? 0.9 : 0.7,
      });
      const wrap = document.createElement("div");
      wrap.style.maxWidth = "280px";
      wrap.style.font = "13px/1.35 system-ui, sans-serif";
      const claimants = (p.claimants || []).map((c) => `<li>${claimantLine(c, p.winner)}</li>`).join("");
      const gaps = (p.uncovered || [])
        .map((g) => `<li>${esc(g.start_cep)}–${esc(g.end_cep)}</li>`)
        .join("");
      wrap.innerHTML = `
        <strong>${esc(p.city)}${p.uf ? `/${esc(p.uf)}` : ""}</strong>
        <div style="color:#546e7a;margin:4px 0 8px">CEP ${esc(p.start_cep)}–${esc(p.end_cep)}</div>
        ${
          p.unattended
            ? `<div style="color:#c62828;font-weight:600;margin-bottom:6px">Cidade sem cerca — nenhum CEP atendido</div>`
            : ""
        }
        ${
          p.unattendedPartial && !p.unattended
            ? `<div style="color:#ef6c00;font-weight:600;margin-bottom:6px">Faixa oficial incompleta</div>`
            : ""
        }
        ${
          p.hadOverlap
            ? `<div style="color:#c62828;font-weight:600;margin-bottom:6px">Sobreposição — o e-commerce usa só o vencedor</div>`
            : ""
        }
        ${
          p.split && !p.overlap && !p.unattended
            ? `<div style="color:#ef6c00;font-weight:600;margin-bottom:6px">Cidade fatiada entre destinos (sem overlap)</div>`
            : ""
        }
        ${
          p.unattended
            ? ""
            : `<div>Vencedor: <strong>${esc(p.winner?.region_key || "—")} → ${esc(
                p.winner?.target_cod_interno || "—"
              )}</strong></div>`
        }
        ${claimants && !p.unattended ? `<ul style="margin:8px 0 8px 16px;padding:0">${claimants}</ul>` : ""}
        ${gaps ? `<div style="margin-top:6px">Não atendido:</div><ul style="margin:4px 0 8px 16px;padding:0">${gaps}</ul>` : ""}
        <button type="button" data-cep="${esc(p.sample_cep || p.winner?.start_cep || p.start_cep)}" style="margin-top:4px;cursor:pointer">Ver na tabela</button>
      `;
      wrap.querySelector("button")?.addEventListener("click", () => {
        onViewRef.current?.(p.sample_cep || p.winner?.start_cep || p.start_cep, p.hadOverlap);
      });
      marker.bindPopup(wrap);
      marker.bindTooltip(
        `${p.city || "CEP"}${p.uf ? `/${p.uf}` : ""}${
          p.unattended ? " · sem cerca" : p.unattendedPartial ? " · incompleta" : p.hadOverlap ? " · conflito" : ""
        }`,
        { direction: "top", opacity: 0.9 }
      );
      marker.addTo(layer);
      bounds.push([p.lat, p.lng]);
    }
    if (bounds.length === 1) {
      map.setView(bounds[0], 7);
    } else if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [28, 28], maxZoom: 7 });
    }
    const t = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(t);
  }, [points]);

  return (
    <div
      ref={hostRef}
      style={{ width: "100%", height: "100%", minHeight: 440, background: "#e8eef2" }}
    />
  );
}
